import {
  useEquipmentCatalogue,
  useOrganisationEquipmentTypes,
  useReplaceOrganisationEquipmentTypes,
} from '@my/directory-api/hooks';
import type {
  EquipmentCatalogueClass,
  EquipmentClassValue,
  OrganisationEquipmentType,
} from '@my/directory-api';
import { ErrorBanner, FormSection, Loading, showToast } from '@my/ui';
import { Check, Edit, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EquipmentDefinitionsSectionProps {
  /** Owning organisation id; the cards stay disabled while this is null. */
  organisationId: string | null;
}

/**
 * Central Configuration block for Equipment Definitions — mirrors the
 * legacy My.Tms.Old `ConfigurationSettingsPage` layout: one section titled
 * "Equipment Definitions" with Trailers and Rigids side-by-side on desktop
 * (and stacked on mobile), each rendered as a card with an **inline** edit
 * mode (no modal popup).
 *
 * Edit lifecycle (matches legacy `EquipmentBlock`):
 *  - View mode: title + count on the left, Edit icon on the right. Body
 *    shows the chips of types the org has opted into.
 *  - Click Edit → enter edit mode for THAT block. The header swaps to
 *    X (cancel) + Check (save), and the body shows the full grouped catalogue
 *    with each type as a togglable chip. The other block's Edit icon
 *    disables while one block is being edited so we can't have two drafts
 *    in flight.
 *  - Cancel discards the draft. Save fires `useReplaceOrganisationEquipmentTypes`
 *    with the draft set; on success we exit edit mode and the per-org
 *    selection cache invalidates.
 *
 * Catalogue is loaded once at the section level and shared between the
 * two blocks via prop drilling — keeps RQ cache hits to one per page open.
 */
export function EquipmentDefinitionsSection({ organisationId }: EquipmentDefinitionsSectionProps) {
  const { t } = useTranslation('configuration');
  const catalogueQuery = useEquipmentCatalogue();
  const selectionQuery = useOrganisationEquipmentTypes(organisationId);
  const replaceMutation = useReplaceOrganisationEquipmentTypes();

  // Which class is currently in edit mode, or `null` for "everything in view".
  // Only one block edits at a time so we don't have to manage two drafts.
  const [editingClass, setEditingClass] = useState<EquipmentClassValue | null>(null);
  const [draft, setDraft] = useState<Set<string>>(() => new Set());

  const trailerCatalogue = useMemo(
    () => findClass(catalogueQuery.data?.classes ?? null, 'trailer'),
    [catalogueQuery.data],
  );
  const rigidCatalogue = useMemo(
    () => findClass(catalogueQuery.data?.classes ?? null, 'rigid'),
    [catalogueQuery.data],
  );

  const trailerSelection = useMemo(
    () => filterSelection(selectionQuery.data, 'Trailer'),
    [selectionQuery.data],
  );
  const rigidSelection = useMemo(
    () => filterSelection(selectionQuery.data, 'Rigid'),
    [selectionQuery.data],
  );

  // Show a single global error banner if either dependency failed — the
  // chips below render best-effort even with a partial payload.
  const errorMessage = catalogueQuery.isError
    ? t('equipment.loadCatalogueFailed', { message: catalogueQuery.error.message })
    : selectionQuery.isError
      ? t('equipment.loadSelectionFailed', { message: selectionQuery.error.message })
      : null;

  const handleEdit = (cls: EquipmentClassValue, currentKeys: string[]) => {
    setEditingClass(cls);
    setDraft(new Set(currentKeys));
  };

  const handleToggleDraft = (key: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancel = () => {
    setEditingClass(null);
    setDraft(new Set());
  };

  const handleSave = async (cls: EquipmentClassValue) => {
    if (!organisationId) return;
    try {
      await replaceMutation.mutateAsync({
        organisationId,
        class: cls,
        catalogueKeys: Array.from(draft),
      });
      showToast({
        type: 'success',
        message:
          cls === 'Trailer'
            ? t('equipment.trailersUpdated')
            : t('equipment.rigidsUpdated'),
      });
      setEditingClass(null);
      setDraft(new Set());
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('equipment.saveFailed'),
      });
    }
  };

  const isBusy = replaceMutation.isPending;

  // Keep both blocks behind a single loading shell — they share data sources
  // and their layout has nothing meaningful to show until both arrive.
  const isLoading = catalogueQuery.isPending || selectionQuery.isPending;

  return (
    <>
      {errorMessage && <ErrorBanner title={t('equipment.errorTitle')} message={errorMessage} />}

      <FormSection title={t('equipment.title')} columns={1}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <EquipmentClassBlock
            title={t('equipment.trailers')}
            catalogueClass={trailerCatalogue}
            selection={trailerSelection}
            organisationId={organisationId}
            isLoading={isLoading}
            isBusy={isBusy}
            isEditing={editingClass === 'Trailer'}
            editDisabledByOther={editingClass !== null && editingClass !== 'Trailer'}
            draft={editingClass === 'Trailer' ? draft : null}
            onEdit={() =>
              handleEdit(
                'Trailer',
                trailerSelection.map((s) => s.catalogueKey),
              )
            }
            onToggleDraft={handleToggleDraft}
            onCancel={handleCancel}
            onSave={() => void handleSave('Trailer')}
          />

          <EquipmentClassBlock
            title={t('equipment.rigids')}
            catalogueClass={rigidCatalogue}
            selection={rigidSelection}
            organisationId={organisationId}
            isLoading={isLoading}
            isBusy={isBusy}
            isEditing={editingClass === 'Rigid'}
            editDisabledByOther={editingClass !== null && editingClass !== 'Rigid'}
            draft={editingClass === 'Rigid' ? draft : null}
            onEdit={() =>
              handleEdit(
                'Rigid',
                rigidSelection.map((s) => s.catalogueKey),
              )
            }
            onToggleDraft={handleToggleDraft}
            onCancel={handleCancel}
            onSave={() => void handleSave('Rigid')}
          />
        </div>
      </FormSection>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Per-class block — card with INLINE edit mode
// ────────────────────────────────────────────────────────────────────────────

interface EquipmentClassBlockProps {
  title: string;
  catalogueClass: EquipmentCatalogueClass | null;
  /** Active rows for this class — what view mode renders as chips. */
  selection: OrganisationEquipmentType[];
  organisationId: string | null;
  isLoading: boolean;
  isBusy: boolean;
  /** True when this block is the one currently being edited. */
  isEditing: boolean;
  /** Disables the Edit icon because the OTHER block is editing right now. */
  editDisabledByOther: boolean;
  /**
   * Working set of catalogue keys while in edit mode; `null` when this block
   * is not the editing one (view mode).
   */
  draft: Set<string> | null;
  onEdit: () => void;
  onToggleDraft: (key: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

function EquipmentClassBlock({
  title,
  catalogueClass,
  selection,
  organisationId,
  isLoading,
  isBusy,
  isEditing,
  editDisabledByOther,
  draft,
  onEdit,
  onToggleDraft,
  onCancel,
  onSave,
}: EquipmentClassBlockProps) {
  const { t } = useTranslation('configuration');
  // Build a label lookup once per render so chip rendering is O(1) per row.
  // If a saved key has somehow drifted off the catalogue (BE removed an
  // entry, which the convention forbids — but defend anyway), fall back to
  // the raw key so the operator can at least see something to remove.
  const labelByKey = useMemo(() => buildLabelMap(catalogueClass), [catalogueClass]);

  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm">
      {/* Header — title + count on the left, icon-only actions on the right.
          Icon button styling mirrors `ServiceTagsSection` so the page reads
          as a single visual family (h-5/w-5 button, h-3/w-3 glyph). */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          {title}
          <span className="text-xs font-normal text-slate-400">({selection.length})</span>
        </h3>

        {!isEditing ? (
          <button
            type="button"
            aria-label={t('equipment.ariaEdit', { name: title })}
            title={t('equipment.ariaEdit', { name: title })}
            onClick={onEdit}
            disabled={!organisationId || isLoading || isBusy || editDisabledByOther}
            className="flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <Edit className="h-3 w-3" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('equipment.ariaCancel')}
              title={t('equipment.ariaCancel')}
              onClick={onCancel}
              disabled={isBusy}
              className="flex h-5 w-5 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
            <button
              type="button"
              aria-label={t('equipment.ariaSave')}
              title={t('equipment.ariaSave')}
              onClick={onSave}
              disabled={isBusy || !catalogueClass}
              className="flex h-5 w-5 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="px-3 py-3">
        {isLoading ? (
          <Loading label={t('equipment.loading')} />
        ) : isEditing ? (
          <EditModeBody
            catalogueClass={catalogueClass}
            draft={draft ?? new Set()}
            isBusy={isBusy}
            onToggleDraft={onToggleDraft}
          />
        ) : selection.length === 0 ? (
          <p className="text-xs italic text-slate-400">{t('equipment.empty')}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selection.map((row) => (
              <span
                key={row.id}
                className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700"
                title={row.catalogueKey}
              >
                {labelByKey.get(row.catalogueKey) ?? row.catalogueKey}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Edit-mode body — full grouped catalogue rendered as togglable chips
// ────────────────────────────────────────────────────────────────────────────

interface EditModeBodyProps {
  catalogueClass: EquipmentCatalogueClass | null;
  draft: Set<string>;
  isBusy: boolean;
  onToggleDraft: (key: string) => void;
}

function EditModeBody({ catalogueClass, draft, isBusy, onToggleDraft }: EditModeBodyProps) {
  const { t } = useTranslation('configuration');
  if (!catalogueClass) {
    return (
      <p className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
        {t('equipment.catalogueUnavailable')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">{t('equipment.selectPrompt')}</p>
      {catalogueClass.groups.map((group) => (
        <div key={group.label}>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {group.label}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {group.types.map((t) => {
              const active = draft.has(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onToggleDraft(t.key)}
                  disabled={isBusy}
                  // Selected = filled sky pill (matches the view-mode chip
                  // colour); unselected = neutral outline.
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                    active
                      ? 'border-sky-600 bg-sky-600 text-white'
                      : 'border-slate-300 bg-white text-slate-600 hover:border-sky-400 hover:text-sky-700'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function findClass(
  classes: EquipmentCatalogueClass[] | null,
  key: string,
): EquipmentCatalogueClass | null {
  if (!classes) return null;
  return classes.find((c) => c.key === key) ?? null;
}

function filterSelection(
  rows: OrganisationEquipmentType[] | undefined,
  cls: EquipmentClassValue,
): OrganisationEquipmentType[] {
  if (!rows) return [];
  return rows.filter((r) => r.class === cls);
}

function buildLabelMap(cls: EquipmentCatalogueClass | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!cls) return map;
  for (const group of cls.groups) {
    for (const type of group.types) {
      map.set(type.key, type.label);
    }
  }
  return map;
}
